import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './StatusTab.css';
import './HealthHourTab.css';
import './ExamsTab.css';
import InfoModal from './InfoModal';
import EXAM_EXPLANATIONS from './examExplanations';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1].slice(2)} ${m[4]}:${m[5]}`;
};

const fmtDateShort = (iso) => {
  if (!iso) return '';
  const [y, mo, d] = iso.slice(0, 10).split('-');
  return `${d}/${mo}/${y.slice(2)}`;
};

const nowLocalInput = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const STATUS_BADGE = { alto: 'bad', baixo: 'low', normal: 'good' };
const STATUS_LABEL = { alto: 'Alto', baixo: 'Baixo', normal: 'Normal' };
const STATUS_COLOR = { alto: '#f87171', baixo: '#7dd3fc', normal: '#4ade80' };
const DEFAULT_COLOR = '#94a3b8';

// Mesmo padrão dos mini gráficos da aba Saúde, mas cada barra usa a cor do
// status daquele ponto (alto/baixo/normal).
function ExamSpark({ values, statuses, labels, displays }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...values.map((v) => v || 0));
  const n = Math.max(1, values.length);
  const W = 100, H = 34, bw = W / n;

  return (
    <div className="hh-spark-wrap">
      <svg
        className="hh-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        {values.map((v, i) => {
          const h = ((v || 0) / max) * H;
          return (
            <rect key={`b${i}`} x={i * bw + bw * 0.2} y={H - h}
              width={bw * 0.6} height={h}
              fill={STATUS_COLOR[statuses[i]] || DEFAULT_COLOR}
              opacity={hover === null || hover === i ? 1 : 0.4} />
          );
        })}
        {values.map((_, i) => (
          <rect key={`h${i}`} x={i * bw} y={0} width={bw} height={H} fill="transparent"
            onMouseEnter={() => setHover(i)} onTouchStart={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && (
        <div className="hh-tip" style={{ left: `${((hover + 0.5) / n) * 100}%` }}>
          <b>{fmtDateTime(labels[hover])}</b> {displays[hover]}
        </div>
      )}
    </div>
  );
}

function ExamCard({ c }) {
  const [showInfo, setShowInfo] = useState(false);
  const cor = STATUS_COLOR[c.status] || DEFAULT_COLOR;
  const explicacao = EXAM_EXPLANATIONS[c.label];
  return (
    <div className="hh-card">
      <div className="hh-card-head">
        <div className="hh-card-head-left">
          <span className="hh-title">{c.label}</span>
          <span className="hh-sub">{c.ref_texto || fmtDateTime(c.reference_date)}</span>
        </div>
        <div className="hh-card-head-right">
          <div className="hh-value-row">
            <span className="hh-value" style={{ color: cor }}>
              {c.value_display}{c.unidade && <small>{c.unidade}</small>}
            </span>
          </div>
          {c.delta_display && <span className="hh-delta">{c.delta_display} vs. anterior</span>}
        </div>
      </div>

      {c.status && (
        <span className={`bc-badge ${STATUS_BADGE[c.status]} ex-badge`}>{STATUS_LABEL[c.status]}</span>
      )}

      <div className="hh-spark-clickarea" onClick={() => setShowInfo(true)} role="button">
        <ExamSpark values={c.spark} statuses={c.spark_status} labels={c.spark_labels} displays={c.spark_display} />
      </div>
      <div className="hh-axis"><span>{fmtDateShort(c.axis[0])}</span><span>{fmtDateShort(c.axis[1])}</span></div>

      {showInfo && (
        <InfoModal title={c.label} onClose={() => setShowInfo(false)}>
          {explicacao && <p>{explicacao}</p>}
          {c.ref_texto && <p>Referência: <b>{c.ref_texto}{c.unidade ? ` ${c.unidade}` : ''}</b></p>}
          <p>Última coleta: {fmtDateTime(c.reference_date)}. Histórico com {c.spark.length} coleta(s), em ordem cronológica.</p>
        </InfoModal>
      )}
    </div>
  );
}

function ExamsTab({ svc }) {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  const [mParametro, setMParametro] = useState('');
  const [mGrupo, setMGrupo] = useState('');
  const [mValor, setMValor] = useState('');
  const [mUnidade, setMUnidade] = useState('');
  const [mData, setMData] = useState(nowLocalInput());
  const [mRefTexto, setMRefTexto] = useState('');
  const [mBusy, setMBusy] = useState(false);
  const [mMsg, setMMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await svc.getExams();
      if (d.error) throw new Error(d.error);
      setGrupos(d.grupos || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  // Além das listas pros combos, guarda os últimos grupo/unidade/referência
  // de cada parâmetro já lançado — selecionar um indicador conhecido
  // preenche o resto sozinho.
  const conhecidos = useMemo(() => {
    const params = new Set(), gruposSet = new Set(), unidadesSet = new Set(), refsSet = new Set();
    const porParametro = {};
    grupos.forEach((g) => {
      gruposSet.add(g.nome);
      g.cards.forEach((c) => {
        params.add(c.label);
        if (c.unidade) unidadesSet.add(c.unidade);
        if (c.ref_texto) refsSet.add(c.ref_texto);
        porParametro[c.label] = { grupo: g.nome, unidade: c.unidade || '', ref_texto: c.ref_texto || '' };
      });
    });
    return {
      parametros: Array.from(params).sort(),
      grupos: Array.from(gruposSet).sort(),
      unidades: Array.from(unidadesSet).sort(),
      referencias: Array.from(refsSet).sort(),
      porParametro,
    };
  }, [grupos]);

  const handleParametroChange = (value) => {
    setMParametro(value);
    const info = conhecidos.porParametro[value];
    if (info) {
      setMGrupo(info.grupo);
      setMUnidade(info.unidade);
      setMRefTexto(info.ref_texto);
    }
  };

  const manualPreenchido = mParametro.trim() && mGrupo.trim() && mUnidade.trim() && mRefTexto.trim() && mValor.trim();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setUploadMsg(null);
    try {
      const d = await svc.uploadExams(file);
      if (d.error) throw new Error(d.error);
      setUploadMsg(`${d.records_saved} resultado(s) importado(s).`);
      await load();
    } catch (e2) { setUploadMsg('Erro: ' + e2.message); } finally { setUploading(false); }
  };

  const addManual = async () => {
    if (!manualPreenchido) return;
    setMBusy(true); setMMsg(null);
    try {
      const d = await svc.createExamResult({
        parametro: mParametro.trim(), grupo: mGrupo.trim(),
        valor: mValor.trim(), unidade: mUnidade.trim(),
        medido_em: mData, ref_texto: mRefTexto.trim(),
      });
      if (d.error) throw new Error(d.error);
      setMParametro(''); setMGrupo(''); setMValor(''); setMUnidade(''); setMRefTexto('');
      setMMsg('✓ Resultado lançado.');
      await load();
    } catch (e) { setMMsg('Erro: ' + e.message); } finally { setMBusy(false); }
  };

  if (loading) return <div className="ex-view"><p className="ef-msg">Carregando…</p></div>;

  return (
    <div className="ex-view">
      <div className="st-card">
        <h3 className="st-title">🧪 Exames laboratoriais</h3>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Envie o Excel consolidado dos seus exames pra ver a evolução de cada parâmetro aqui.
          Reenviar o arquivo atualiza os resultados sem duplicar.
        </p>
        <label className="ef-add-btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
          {uploading ? 'Enviando…' : '📤 Enviar Excel (.xlsx)'}
          <input type="file" accept=".xlsx" onChange={handleFile} disabled={uploading} style={{ display: 'none' }} />
        </label>
        {uploadMsg && <p className="ef-msg" style={{ marginTop: 8 }}>{uploadMsg}</p>}
      </div>

      <div className="st-card">
        <h3 className="st-title">✍️ Lançar resultado manualmente</h3>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Escolha um indicador já usado antes pra preencher grupo, unidade e
          referência sozinho — ou digite um novo, que fica salvo pra próxima vez.
          Só salva com todos os campos preenchidos.
        </p>
        <div className="hh-form">
          <input
            className="hh-input hh-input-wide" list="ex-parametros" placeholder="Indicador (ex: Glicose)"
            value={mParametro} onChange={(e) => handleParametroChange(e.target.value)}
          />
          <datalist id="ex-parametros">
            {conhecidos.parametros.map((p) => <option key={p} value={p} />)}
          </datalist>
          <input
            className="hh-input" list="ex-grupos" placeholder="Grupo"
            value={mGrupo} onChange={(e) => setMGrupo(e.target.value)}
          />
          <datalist id="ex-grupos">
            {conhecidos.grupos.map((g) => <option key={g} value={g} />)}
          </datalist>
        </div>
        <div className="hh-form">
          <input
            className="hh-input" type="text" inputMode="decimal" placeholder="Valor"
            value={mValor} onChange={(e) => setMValor(e.target.value)} style={{ maxWidth: 110 }}
          />
          <input
            className="hh-input" list="ex-unidades" placeholder="Unidade" value={mUnidade}
            onChange={(e) => setMUnidade(e.target.value)} style={{ maxWidth: 110 }}
          />
          <datalist id="ex-unidades">
            {conhecidos.unidades.map((u) => <option key={u} value={u} />)}
          </datalist>
          <input
            className="hh-input" list="ex-referencias" placeholder="Referência (ex: 70 – 99)"
            value={mRefTexto} onChange={(e) => setMRefTexto(e.target.value)}
          />
          <datalist id="ex-referencias">
            {conhecidos.referencias.map((r) => <option key={r} value={r} />)}
          </datalist>
        </div>
        <div className="hh-form">
          <input
            className="hh-input" type="datetime-local" value={mData}
            onChange={(e) => setMData(e.target.value)}
          />
          <button className="hh-btn" onClick={addManual} disabled={mBusy || !manualPreenchido}>
            {mBusy ? '…' : 'Adicionar'}
          </button>
        </div>
        {mMsg && <p className="ef-msg" style={{ marginTop: 4 }}>{mMsg}</p>}
      </div>

      {error && <p className="ef-msg ef-err">{error}</p>}
      {!error && grupos.length === 0 && (
        <p className="ef-msg">Nenhum exame lançado ainda.</p>
      )}

      {grupos.map((g) => (
        <div key={g.nome}>
          <div className="hh-subtitle">{g.nome}</div>
          <div className="hh-grid">
            {g.cards.map((c) => <ExamCard key={c.key} c={c} />)}
          </div>
        </div>
      ))}
      <div className="spacer"></div>
    </div>
  );
}

export default ExamsTab;
